/**
 * Subscription, a page like the other Home pages (the app's PanelSubscriptions
 * texts and icons, not its popup look): the ten benefits, the Monthly and
 * Annual plans (Annual chosen first, "Best value") and the button that starts
 * the purchase ("Try free for 7 days" when the plan has a trial). Buying goes
 * through RevenueCat Billing's checkout (data/subscription.ts). A Premium user
 * (App Store, Google Play, the web, a school licence) sees the subscription,
 * where to manage it, and the benefits as what the plan includes.
 *
 * Below, the school packs (Teams, 5 teachers; Schools, 20; annual, no trial),
 * as on the landing: buying one makes a school licence its buyer manages
 * (functions/src/schools.ts), and the page leads to "Manage teachers".
 */
import { useEffect, useMemo, useRef, useState } from "react";
import type { Package } from "@revenuecat/purchases-js";
import { loadAppTexts, ut } from "../editor/appText";
import { currentLanguage, t } from "../player/i18n";
import { RTL_LANGUAGES } from "../player/rtl";
import type { Account } from "../data/myProjects";
import type { AuthUser } from "../data/auth";
import { buy, FREE, isPremium, managementUrl, RC_SANDBOX, RC_WEB_KEY, refreshSubscription, webPlans, type Subscription, type WebPlans } from "../data/subscription";
import { AppAlert } from "./AppAlert";
import { LINKS } from "./Landing";
import { landingText } from "./landingText";
import { SHOW_PACKS_KEY } from "./localPrice";
import { st } from "./schoolText";
import { Icon } from "./widgets";

/** sLine1…sLine10 and their icons (TextColumns › Left / Right). */
const BENEFITS: [string, string][] = [
  ["sLine1", "games"],
  ["sLine2", "share"],
  ["sLine3", "class"],
  ["sLine4", "devices"],
  ["sLine5", "progress"],
  ["sLine6", "brain"],
  ["sLine7", "home-empty"],
  ["sLine8", "lesson"],
  ["sLine9", "rocket"],
  ["sLine10", "power"],
];

const WEB_STORES = new Set(["rc_billing", "stripe", "paddle"]);
const STORE_NAMES: Record<string, string> = { app_store: "App Store", mac_app_store: "Mac App Store", play_store: "Google Play", amazon: "Amazon" };

export function SubscriptionView({ user, account }: { user: AuthUser; account: Account | null }) {
  const lang = currentLanguage();
  const L = landingText();
  const [ready, setReady] = useState(false);
  const [plans, setPlans] = useState<WebPlans | null>(null);
  const [plansError, setPlansError] = useState(false);
  const [chosen, setChosen] = useState<"annual" | "monthly">("annual");
  const [busy, setBusy] = useState<null | "buy" | "teams" | "schools" | "check" | "manage">(null);
  const [alert, setAlert] = useState<null | "done" | "packDone" | "failed" | "notFound">(null);
  const sub = account?.subscription ?? null;
  const premium = isPremium(sub);
  // testers in sandbox mode can buy (Stripe test mode) even when already Premium
  const showPlans = !premium || RC_SANDBOX;
  // the packs, for anyone who does not manage a school yet (an Educators subscriber too)
  const showPacks = !(sub?.school?.role === "manager" && sub.school.expiresAt > Date.now()) || RC_SANDBOX;
  const packsRef = useRef<HTMLElement>(null);

  useEffect(() => {
    void loadAppTexts().then(() => setReady(true));
  }, []);
  // ask again when the page opens (an app subscriber who never came here, a renewal…)
  useEffect(() => {
    const checked = sub?.checkedAt ?? 0;
    if (Date.now() - checked > 60 * 60 * 1000) void refreshSubscription().catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.uid]);
  const devPlans = import.meta.env.DEV && location.search.includes("devplans");
  useEffect(() => {
    if (devPlans) {
      const fake = (price: string) => ({ webBillingProduct: { price: { formattedPrice: price }, freeTrialPhase: { periodDuration: "P7D" } } }) as unknown as Package;
      setPlans({ monthly: fake("9,99 €"), annual: fake("49,99 €"), teams: fake("149 €"), schools: fake("299 €") });
      return;
    }
    if (!RC_WEB_KEY || !(showPlans || showPacks)) return;
    let gone = false;
    webPlans(user.uid)
      .then((p) => !gone && setPlans(p))
      .catch(() => !gone && setPlansError(true));
    return () => {
      gone = true;
    };
  }, [user.uid, showPlans, showPacks]);
  // from the landing's Teams or Schools: straight to the packs
  useEffect(() => {
    try {
      if (!ready || !plans || sessionStorage.getItem(SHOW_PACKS_KEY) !== "1") return;
      sessionStorage.removeItem(SHOW_PACKS_KEY);
    } catch {
      return;
    }
    packsRef.current?.scrollIntoView({ block: "start" });
  }, [ready, plans]);

  const pkg: Package | null = plans ? (chosen === "annual" ? plans.annual : plans.monthly) ?? plans.annual ?? plans.monthly : null;
  const trial = !!pkg?.webBillingProduct.freeTrialPhase;

  const purchase = async (what: "buy" | "teams" | "schools" = "buy") => {
    const chosenPkg = what === "buy" ? pkg : plans?.[what];
    if (!chosenPkg || busy) return;
    setBusy(what);
    try {
      const bought = await buy(user.uid, chosenPkg, { email: user.email, locale: lang });
      if (bought) {
        const s = await refreshSubscription().catch(() => null);
        // a pack: its school is there to add the teachers to (or comes with RevenueCat's webhook a moment later)
        setAlert(what !== "buy" && s?.school?.role === "manager" ? "packDone" : "done");
      }
    } catch (err) {
      console.warn("purchase failed", err);
      setAlert("failed");
    } finally {
      setBusy(null);
    }
  };
  const check = async () => {
    if (busy) return;
    setBusy("check");
    try {
      const s = await refreshSubscription();
      if (!isPremium(s)) setAlert("notFound");
    } catch {
      setAlert("notFound");
    } finally {
      setBusy(null);
    }
  };
  const manage = async () => {
    if (busy) return;
    setBusy("manage");
    try {
      const url = sub?.managementUrl || (await managementUrl(user.uid));
      if (url) window.open(url, "_blank", "noopener");
    } finally {
      setBusy(null);
    }
  };

  if (!ready) return <section className="view" />;
  return (
    <section className="view sub-view" dir={RTL_LANGUAGES.has(lang) ? "rtl" : undefined}>
      <h1>
        {x("subscription")}
        {RC_SANDBOX && <span className="sub-sandbox">SANDBOX</span>}
      </h1>
      {premium && sub && <Status sub={sub} lang={lang} onManage={() => void manage()} busy={busy === "manage"} />}
      <h2 className="sub-title">{premium ? t("subIncluded") : x("sSubscribeTitle")}</h2>
      <ul className="sub-benefits">
        {BENEFITS.map(([key, icon]) => (
          <li key={key} className="sub-benefit">
            <Icon name={icon} />
            <span>{x(key)}</span>
          </li>
        ))}
      </ul>
      {showPlans && (
        <>
          {premium && <p className="sub-sandbox-note muted">{t("subSandboxNote")}</p>}
          {(RC_WEB_KEY || devPlans) && !plansError ? (
            <>
              <div className="sub-plans" role="radiogroup">
                <PlanCard label={x("sPlanMonthly")} price={plans?.monthly ? `${priceText(plans.monthly, lang)}/${x("sMonth")}` : plans ? "—" : "…"} on={chosen === "monthly"} onPick={() => setChosen("monthly")} disabled={!!plans && !plans.monthly} />
                <PlanCard label={x("sPlanAnnual")} price={plans?.annual ? `${priceText(plans.annual, lang)}/${x("sYear")}` : plans ? "—" : "…"} on={chosen === "annual"} onPick={() => setChosen("annual")} best={x("sBestValue")} disabled={!!plans && !plans.annual} />
              </div>
              <div className="sub-buy-wrap">
                <button className="btn sub-buy" onClick={() => void purchase("buy")} disabled={!pkg || !!busy}>
                  {busy === "buy" ? "…" : trial ? x("sStartFree2") : x("accSubscribe")}
                </button>
                <span className="sub-cancel muted">{x("sBenefitCancel")}</span>
              </div>
            </>
          ) : (
            <p className="sub-unavailable">{t("subUnavailable")}</p>
          )}
          {!premium && <p className="sub-free muted">{t("subFreeLimits").replace("{projects}", String(FREE.projects)).replace("{classrooms}", String(FREE.classrooms)).replace("{folders}", String(FREE.folders))}</p>}
        </>
      )}
      {showPacks && (RC_WEB_KEY || devPlans) && !plansError && (!plans || plans.teams || plans.schools) && (
        <section className="sub-packs" ref={packsRef}>
          <h2 className="sub-title">{t("subPacksTitle")}</h2>
          <p className="muted">{t("subPacksLine")}</p>
          <div className="sub-pack-list">
            {(["teams", "schools"] as const).map((k) => {
              const p = plans?.[k] ?? null;
              return (
                <div key={k} className="sub-pack" data-testid={`sub-pack-${k}`}>
                  <span className="sub-plan-name">{L.plans[k]}</span>
                  <span className="sub-plan-price">{p ? `${priceText(p, lang)}/${x("sYear")}` : plans ? "—" : "…"}</span>
                  <ul>
                    {L.plans[k === "teams" ? "teamsFeatures" : "schoolsFeatures"].map((f) => (
                      <li key={f}>{f}</li>
                    ))}
                  </ul>
                  <button className="btn" onClick={() => void purchase(k)} disabled={!p || !!busy}>
                    {busy === k ? "…" : L.plans.subscribe}
                  </button>
                </div>
              );
            })}
          </div>
        </section>
      )}
      <div className="sub-info">
        {!premium && RC_WEB_KEY && <p>{t("subWebTerms")}</p>}
        {!premium && (
          <button className="link-btn" onClick={() => void check()} disabled={!!busy}>
            {busy === "check" ? t("subChecking") : t("subCheck")}
          </button>
        )}
        <p className="sub-schools">
          <strong>{t("subPacksTitle")}</strong> · {t("subSchools").replace("{email}", LINKS.email)}
        </p>
        <p className="sub-links">
          <a href={LINKS.privacy} target="_blank" rel="noreferrer">
            {t("privacyPolicy")}
          </a>
          <a href={LINKS.terms} target="_blank" rel="noreferrer">
            {t("termsOfUse")}
          </a>
        </p>
      </div>
      {alert === "done" && <AppAlert title={x("subscriptionOkTitle")} message={x("sPurchaseCompleted")} okLabel={x("ok")} onOk={() => setAlert(null)} />}
      {alert === "packDone" && (
        <AppAlert
          title={x("subscriptionOkTitle")}
          message={t("subPackDone")}
          okLabel={st("manage")}
          onOk={() => {
            setAlert(null);
            location.hash = "#/school";
          }}
        />
      )}
      {alert === "failed" && <AppAlert title={x("defaultErrorTitle")} message={`${x("sPurchaseFailed")} ${LINKS.email}`} okLabel={x("ok")} onOk={() => setAlert(null)} />}
      {alert === "notFound" && <AppAlert title={x("subscription")} message={t("subNotFound")} okLabel={x("ok")} onOk={() => setAlert(null)} />}
    </section>
  );
}

/** RevenueCat's price in the interface's language ("29,99 zł": its own formattedPrice is always in English). */
function priceText(pkg: Package, lang: string): string {
  const p = pkg.webBillingProduct.price;
  try {
    // a round price without decimals ("449 zł"), as on the landing
    const amount = p.amountMicros / 1e6;
    if (typeof p.amountMicros === "number" && p.currency) return new Intl.NumberFormat(lang, { style: "currency", currency: p.currency, minimumFractionDigits: Number.isInteger(amount) ? 0 : undefined }).format(amount);
  } catch {
    /* an unknown currency: RevenueCat's own text */
  }
  return p.formattedPrice;
}

function PlanCard({ label, price, on, onPick, best, disabled }: { label: string; price: string; on: boolean; onPick: () => void; best?: string; disabled?: boolean }) {
  return (
    <button className={`sub-plan${on ? " on" : ""}`} role="radio" aria-checked={on} onClick={onPick} disabled={disabled}>
      <span className="sub-plan-name">{label}</span>
      <span className="sub-plan-price">{price}</span>
      {best && <span className="sub-best">{best}</span>}
    </button>
  );
}

function Status({ sub, lang, onManage, busy }: { sub: Subscription; lang: string; onManage: () => void; busy: boolean }) {
  const date = useMemo(() => (sub.expiresAt ? new Intl.DateTimeFormat(lang, { dateStyle: "long" }).format(sub.expiresAt) : ""), [sub.expiresAt, lang]);
  const when = sub.expiresAt === null ? t("subLifetime") : sub.trial && sub.willRenew ? t("subTrialEnds").replace("{date}", date) : sub.willRenew ? t("subRenews").replace("{date}", date) : t("subEnds").replace("{date}", date);
  const web = !!sub.store && WEB_STORES.has(sub.store);
  const storeName = sub.store ? STORE_NAMES[sub.store] : undefined;
  return (
    <div className="sub-status">
      <Icon name="subscription" />
      <div className="sub-status-text">
        <h2>{t("subActive")}</h2>
        <p>{when}</p>
        {sub.school?.role === "manager" && (
          <p className="muted">
            {st("managerLine")} <a href="#/school">{st("manage")}</a>
          </p>
        )}
        {sub.school?.role === "manager" && sub.source === "school" ? null : sub.schoolAdminEmail ? (
          <p className="muted">{t("subSchool").replace("{email}", sub.schoolAdminEmail)}</p>
        ) : web ? (
          <p className="muted">{t("subFrom").replace("{store}", t("subStoreWeb"))}</p>
        ) : storeName ? (
          <p className="muted">{t("subManageStore").replace("{store}", storeName)}</p>
        ) : sub.source === "legacy" ? (
          <p className="muted">{t("subFrom").replace("{store}", t("subStoreApp"))}</p>
        ) : null}
      </div>
      {web && (
        <button className="btn" onClick={onManage} disabled={busy}>
          {busy ? "…" : t("subManage")}
        </button>
      )}
    </div>
  );
}

/** The app's texts (ut), with Hebrew, which the app's XML does not have. */
function x(id: string): string {
  return (currentLanguage() === "he" && SUB_HE[id]) || ut(id);
}

const SUB_HE: Record<string, string> = {
  sSubscribeTitle: "להפוך את הלמידה למהנה ומרתקת",
  sLine1: "יצירת משחקים וחידונים",
  sLine2: "שיתוף הפרויקטים שלך",
  sLine3: "ניהול הכיתות שלך",
  sLine4: "בכל המכשירים שלך",
  sLine5: "מעקב אחר התקדמות התלמידים",
  sLine6: "חיזוק הידע",
  sLine7: "להגיע לתלמידים שאינם בבית הספר",
  sLine8: "יצירת שיעורים אינטראקטיביים",
  sLine9: "עידוד היצירתיות",
  sLine10: "העצמת בית הספר שלך",
  sPlanMonthly: "תוכנית חודשית",
  sPlanAnnual: "תוכנית שנתית",
  sBestValue: "המשתלם ביותר",
  sStartFree2: "נסו בחינם 7 ימים",
  accSubscribe: "להרשמה",
  sBenefitCancel: "אפשר לבטל בכל עת",
  sMonth: "חודש",
  sYear: "שנה",
  subscriptionOkTitle: "המינוי הושלם",
  sPurchaseCompleted: "מזל טוב, נרשמת בהצלחה!",
  sPurchaseFailed: "אופס! אירעה שגיאה ברכישה. לא חויבת בדבר. בדקו את החיבור לאינטרנט ונסו שוב. אם הבעיה נמשכת, כתבו לנו לכתובת",
  defaultErrorTitle: "שגיאה",
  subscription: "מינוי",
  mhSubscribe: "להרשמה",
  sNotSubscribedMessage: "הירשמו כדי לפתוח את כל התכונות.",
  sNotSubscribedMessageProjects: "בגרסה החינמית אפשר ליצור רק 2 פרויקטים.",
  sNotSubscribedMessageClassrooms: "בגרסה החינמית אפשר ליצור רק כיתה אחת.",
  sNotSubscribedMessageFolders: "בגרסה החינמית אפשר ליצור רק תיקייה אחת.",
  imageSearchNotSubscribed: "חיפוש תמונות באינטרנט זמין רק למשתמשים עם מינוי פעיל.",
  ok: "אישור",
};

/** The app's texts for the free-limit alert and others outside this page. */
export const subText = x;
